<template>
    <div class="page-container">
        <!-- 头部导航 -->
        <header class="header" :class="{ 'header-fixed': isScrolled }">
            <div class="header-content">
                <!-- Logo -->
                <div class="logo" @click="goHome">
                    <img src="@/assets/logo.png" alt="互联造物SAAS" class="logo-img">
                </div>
                
                <!-- 导航菜单 -->
                <nav class="nav-menu hidden-xs">
                    <a 
                        v-for="(item, index) in navList" 
                        :key="index"
                        :class="{ active: currentNav === index }"
                        @click="handleNav(index)"
                        class="nav-item"
                    >
                        {{ item }}
                    </a>
                </nav>
                
                <!-- 移动端菜单按钮 -->
                <div class="menu-toggle hidden-sm hidden-md hidden-lg" @click="toggleMobileMenu">
                    <Icon :type="mobileMenuOpen ? 'ios-close' : 'ios-menu'" size="28" />
                </div>
            </div>
            
            <!-- 移动端菜单 -->
            <div v-show="mobileMenuOpen" class="mobile-menu">
                <a 
                    v-for="(item, index) in navList" 
                    :key="index"
                    :class="{ active: currentNav === index }"
                    @click="handleMobileNav(index)"
                    class="mobile-nav-item"
                >
                    {{ item }}
                </a>
            </div>
        </header>

        <!-- 轮播图 -->
        <section class="banner-section">
            <Carousel 
                radius-dot 
                dots="inside" 
                :autoplay="bannerSettings.autoplay"
                :autoplay-speed="bannerSettings.autoplaySpeed"
                :loop="bannerSettings.loop"
                @on-change="handleBannerChange"
            >
                <CarouselItem v-for="(banner, index) in bannerList" :key="index">
                    <div class="banner-item" :style="{ backgroundImage: `url(${banner.image})` }">
                        <div class="banner-content">
                            <h2 class="banner-title">{{ banner.title }}</h2>
                            <p class="banner-desc">{{ banner.description }}</p>
                            <Button type="primary" size="large" class="banner-btn">
                                {{ banner.buttonText }}
                            </Button>
                        </div>
                    </div>
                </CarouselItem>
            </Carousel>
        </section>

        <!-- 公司简介 -->
        <section class="section about-section">
            <div class="container">
                <div class="section-title">
                    <h2>关于我们</h2>
                    <p>ABOUT US</p>
                </div>
                <!-- 第一行：图片在左，文字在右 -->
                <div class="split-row reverse">
                    <div class="split-image-wrap">
                        <div class="split-image-box">
                            <div class="image-decoration"></div>
                            <div class="image-placeholder">
                                <Icon type="ios-globe" size="80" />
                            </div>
                        </div>
                    </div>
                    <div class="split-text">
                        <div class="split-tag">01 / INTRODUCE</div>
                        <h3>互联造物SAAS</h3>
                        <p class="highlight">链接万物，创造未来</p>
                        <p>安徽勺子同学科技有限公司是一家专注于云计算服务与技术创新的科技企业。公司致力于为企业和个人用户提供安全、稳定、高效的云端解决方案，助力数字化转型。</p>
                        <p>我们秉承"创新、专业、诚信、共赢"的理念，不断探索云计算技术的最新发展，为客户创造更大的价值。</p>
                        <div class="split-stats">
                            <div class="split-stat-item">
                                <strong>100+</strong>
                                <span>服务客户</span>
                            </div>
                            <div class="split-stat-item">
                                <strong>5年+</strong>
                                <span>行业经验</span>
                            </div>
                            <div class="split-stat-item">
                                <strong>99.9%</strong>
                                <span>服务可用性</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 核心业务 -->
        <section class="section service-section">
            <div class="container">
                <div class="section-title">
                    <h2>核心业务</h2>
                    <p>OUR SERVICES</p>
                </div>
                <Row :gutter="30">
                    <Col :xs="24" :sm="12" :md="6" :lg="6" v-for="(service, index) in serviceList" :key="index">
                        <div class="service-card">
                            <div class="service-icon">
                                <Icon :type="service.icon" size="48" />
                            </div>
                            <h3>{{ service.title }}</h3>
                            <p>{{ service.description }}</p>
                            <div class="service-hover">
                                <Icon :type="service.icon" size="48" />
                                <h3>{{ service.title }}</h3>
                                <p>{{ service.description }}</p>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 我们的优势 -->
        <section class="section advantage-section">
            <div class="container">
                <div class="section-title">
                    <h2>我们的优势</h2>
                    <p>OUR ADVANTAGES</p>
                </div>
                <Row :gutter="30">
                    <Col :xs="24" :sm="12" :md="6" :lg="6" v-for="(advantage, index) in advantageList" :key="index">
                        <div class="advantage-card">
                            <div class="advantage-number">{{ String(index + 1).padStart(2, '0') }}</div>
                            <h3>{{ advantage.title }}</h3>
                            <p>{{ advantage.description }}</p>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 电子合同 -->
        <section class="section product-section" id="electronic-contract">
            <div class="container">
                <div class="section-title">
                    <h2>电子合同</h2>
                    <p>ELECTRONIC CONTRACT</p>
                </div>
                <!-- 第二行：文字在左，图片在右 -->
                <div class="split-row">
                    <div class="split-text">
                        <div class="split-tag">02 / PRODUCT</div>
                        <h3>便捷高效的电子签章服务</h3>
                        <p class="highlight">一站式合同签署、管理、存储</p>
                        <p>我们的电子合同系统提供一站式合同签署、管理、存储服务。支持多种签署方式，合法合规、安全可靠，助力企业数字化转型。</p>
                        <ul class="product-features modern">
                            <li><span class="feature-dot"></span> 在线签署，随时随地完成签约</li>
                            <li><span class="feature-dot"></span> 多种签署方式，满足不同场景需求</li>
                            <li><span class="feature-dot"></span> 区块链存证，法律效力有保障</li>
                            <li><span class="feature-dot"></span> 智能合同管理，高效便捷</li>
                        </ul>
                        <Button type="primary" size="large" class="modern-btn">立即体验</Button>
                    </div>
                    <div class="split-image-wrap">
                        <div class="split-image-box">
                            <div class="image-decoration"></div>
                            <div class="image-placeholder">
                                <Icon type="ios-document" size="80" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- 联系我们 -->
        <section class="section contact-section">
            <div class="container">
                <div class="section-title">
                    <h2>联系我们</h2>
                    <p>CONTACT US</p>
                </div>
                <Row :gutter="60">
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="contact-info">
                            <div class="contact-item">
                                <Icon type="ios-location" size="24" class="contact-icon" />
                                <div>
                                    <h4>地址</h4>
                                    <p>{{ companyInfo.address }}</p>
                                </div>
                            </div>
                            <div class="contact-item">
                                <Icon type="ios-call" size="24" class="contact-icon" />
                                <div>
                                    <h4>电话</h4>
                                    <p>{{ companyInfo.tel }}</p>
                                </div>
                            </div>
                            <div class="contact-item">
                                <Icon type="ios-mail" size="24" class="contact-icon" />
                                <div>
                                    <h4>邮箱</h4>
                                    <p>{{ companyInfo.email }}</p>
                                </div>
                            </div>
                            <div class="contact-item">
                                <Icon type="ios-time" size="24" class="contact-icon" />
                                <div>
                                    <h4>工作时间</h4>
                                    <p>周一至周五 9:00-18:00</p>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col :xs="24" :sm="24" :md="12" :lg="12">
                        <div class="contact-qrcode">
                            <!-- <img src="@/assets/WeChat.png" alt="微信公众号" class="qrcode-img"> -->
                            <p>扫码关注我们</p>
                            <p class="qrcode-tip">获取更多资讯</p>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>

        <!-- 底部 -->
        <footer class="footer">
            <div class="container">
                <Row :gutter="40">
                    <Col :xs="24" :sm="24" :md="8" :lg="8">
                        <div class="footer-brand">
                            <img src="@/assets/logo.png" alt="logo" class="footer-logo">
                            <p>{{ companyInfo.name }}</p>
                        </div>
                    </Col>
                    <Col :xs="12" :sm="8" :md="4" :lg="4">
                        <div class="footer-links">
                            <h4>产品服务</h4>
                            <ul>
                                <li><a href="#">云服务器</a></li>
                                <li><a href="#">数据存储</a></li>
                                <li><a href="#">安全服务</a></li>
                                <li><a href="#">技术支持</a></li>
                            </ul>
                        </div>
                    </Col>
                    <Col :xs="12" :sm="8" :md="4" :lg="4">
                        <div class="footer-links">
                            <h4>关于我们</h4>
                            <ul>
                                <li><a href="#">公司介绍</a></li>
                                <li><a href="#">企业文化</a></li>
                                <li><a href="#">发展历程</a></li>
                                <li><a href="#">加入我们</a></li>
                            </ul>
                        </div>
                    </Col>
                    <Col :xs="24" :sm="8" :md="8" :lg="8">
                        <div class="footer-contact">
                            <h4>联系我们</h4>
                            <p><Icon type="ios-call-outline" /> {{ companyInfo.tel }}</p>
                            <p><Icon type="ios-mail-outline" /> {{ companyInfo.email }}</p>
                        </div>
                    </Col>
                </Row>
                <div class="footer-bottom">
                    <p>© {{ currentYear }} {{ companyInfo.name }} 版权所有 | 
                       <a href="#">隐私政策</a> | 
                       <a href="#">服务条款</a>
                    </p>
                </div>
            </div>
        </footer>
        
        <!-- 返回顶部 -->
        <BackTop :bottom="80" :right="30" :duration="1000">
            <div class="back-top">
                <Icon type="ios-arrow-up" size="24" />
            </div>
        </BackTop>
    </div>
</template>

<script>
/**
 * 主布局组件
 * @description 官网首页主布局，包含头部、轮播图、内容区块和底部
 */
import { windowsRise } from '@/utils/dom'; // 引入优化后的工具函数

export default {
    name: 'MainLayout',
    
    data() {
        return {
            // 滚动相关
            isScrolled: false,
            mobileMenuOpen: false,
            currentNav: 0,
            
            // 导航列表
            navList: ['首页', '关于我们', '电子合同', '联系我们'],
            
            // 轮播图配置
            bannerSettings: {
                autoplay: true,
                autoplaySpeed: 5000,
                loop: true
            },
            
            // 轮播图列表
            bannerList: [
                {
                    image: require('@/assets/banner2.jpg'),
                    title: '专业技术支持',
                    description: '7×24小时专业技术团队，随时响应您的需求',
                    buttonText: '立即咨询'
                },
                {
                    image: require('@/assets/banner3.jpg'),
                    title: '数据安全保障',
                    description: '全方位数据安全防护，保障您的业务安全',
                    buttonText: '安全咨询'
                }
            ],
            
            // 业务列表
            serviceList: [
                {
                    icon: 'ios-cloud',
                    title: '云服务器',
                    description: '提供安全、稳定、高效的云服务器服务，满足企业各种业务需求'
                },
                {
                    icon: 'ios-folder',
                    title: '数据存储',
                    description: '企业级数据存储解决方案，保障数据安全与可靠性'
                },
                {
                    icon: 'ios-shield',
                    title: '安全服务',
                    description: '全方位安全防护，保障云端业务安全稳定运行'
                },
                {
                    icon: 'ios-construct',
                    title: '技术支持',
                    description: '7×24小时专业技术支持，快速响应客户需求'
                },
                {
                    icon: 'ios-document',
                    title: '电子合同',
                    description: '便捷高效的电子签章服务，合法合规、安全可靠'
                },
            ],
            
            // 优势列表
            advantageList: [
                {
                    title: '专业团队',
                    description: '拥有经验丰富的技术研发团队，持续创新'
                },
                {
                    title: '稳定可靠',
                    description: '提供7×24小时技术支持，保障业务连续性'
                },
                {
                    title: '灵活定制',
                    description: '根据客户需求提供个性化解决方案'
                },
                {
                    title: '高性价比',
                    description: '让更多企业享受优质云服务'
                }
            ],
            
            // 公司信息
            companyInfo: {
                name: '安徽勺子同学科技有限公司',
                tel: '400-888-8888',
                email: 'info@hulianzaowu.com',
                address: '安徽省'
            }
        };
    },
    
    computed: {
        /**
         * 获取当前年份
         */
        currentYear() {
            return new Date().getFullYear();
        }
    },
    
    mounted() {
        // 初始化
        this.init();
    },
    
    beforeDestroy() {
        // 清理工作
        this.cleanup();
    },
    
    methods: {
        /**
         * 初始化
         */
        init() {
            // 监听窗口滚动
            this.unwatchResize = windowsRise(window, this.handleScroll);
            
            // 初始检查滚动位置
            this.handleScroll();
        },
        
        /**
         * 清理工作
         */
        cleanup() {
            if (this.unwatchResize) {
                this.unwatchResize();
            }
        },
        
        /**
         * 处理滚动事件
         */
        handleScroll() {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            this.isScrolled = scrollTop > 50;
        },
        
        /**
         * 导航切换
         * @param {Number} index 导航索引
         */
        handleNav(index) {
            this.currentNav = index;
            this.scrollToSection(index);
        },
        
        /**
         * 移动端导航切换
         * @param {Number} index 导航索引
         */
        handleMobileNav(index) {
            this.currentNav = index;
            this.mobileMenuOpen = false;
            this.scrollToSection(index);
        },
        
        /**
         * 滚动到指定区块
         * @param {Number} index 区块索引
         */
        scrollToSection(index) {
            // 根据实际页面结构调整滚动逻辑
            const sections = document.querySelectorAll('.section');
            if (sections[index]) {
                sections[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        },
        
        /**
         * 切换移动端菜单
         */
        toggleMobileMenu() {
            this.mobileMenuOpen = !this.mobileMenuOpen;
        },
        
        /**
         * 返回首页
         */
        goHome() {
            this.currentNav = 0;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        
        /**
         * 轮播图切换回调
         * @param {Number} oldIndex 旧索引
         * @param {Number} newIndex 新索引
         */
        handleBannerChange(oldIndex, newIndex) {
            // 可以在这里添加轮播图切换的逻辑
        }
    }
};
</script>

<style scoped>
/* 容器 */
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

/* 头部导航 - 科技风格 */
.header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    background: rgba(10, 15, 26, 0.85);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(0, 195, 255, 0.2);
    transition: all 0.3s;
}

.header-fixed {
    background: rgba(10, 15, 26, 0.95);
    border-bottom: 1px solid rgba(0, 195, 255, 0.3);
    box-shadow: 0 0 30px rgba(0, 195, 255, 0.1);
}

.header-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 15px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.logo {
    display: flex;
    align-items: center;
    cursor: pointer;
}

.logo-img {
    width: 100%;
    height: 40px;
    margin-right: 10px;
    filter: drop-shadow(0 0 10px rgba(0, 195, 255, 0.5));
}

.nav-menu {
    display: flex;
    gap: 40px;
}

.nav-item {
    font-size: 16px;
    color: #e0e6ed;
    cursor: pointer;
    padding: 8px 16px;
    border-radius: 4px;
    transition: all 0.3s;
    position: relative;
}

.nav-item:hover {
    color: #00c3ff;
    text-shadow: 0 0 20px rgba(0, 195, 255, 0.8);
}

.nav-item.active {
    color: #00c3ff;
    font-weight: bold;
    text-shadow: 0 0 20px rgba(0, 195, 255, 0.8);
}

.nav-item.active::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 30px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #00c3ff, transparent);
    box-shadow: 0 0 10px #00c3ff;
}

.menu-toggle {
    cursor: pointer;
    padding: 5px;
    color: #e0e6ed;
}

.mobile-menu {
    background: rgba(10, 15, 26, 0.95);
    backdrop-filter: blur(20px);
    padding: 20px;
    border-top: 1px solid rgba(0, 195, 255, 0.2);
}

.mobile-nav-item {
    display: block;
    padding: 15px 0;
    color: #e0e6ed;
    font-size: 16px;
    text-align: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-nav-item:last-child {
    border-bottom: none;
}

.mobile-nav-item.active {
    color: #00c3ff;
    font-weight: bold;
    text-shadow: 0 0 10px rgba(0, 195, 255, 0.5);
}

/* 轮播图 */
.banner-section {
    margin-top: 70px;
}

.banner-item {
    height: 500px;
    background-size: cover;
    background-position: center;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.banner-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(10, 15, 26, 0.9) 0%, rgba(20, 30, 50, 0.8) 50%, rgba(0, 50, 80, 0.85) 100%);
}

.banner-content {
    text-align: center;
    color: #fff;
    max-width: 800px;
    padding: 0 20px;
    position: relative;
    z-index: 1;
}

.banner-title {
    font-size: 48px;
    font-weight: bold;
    margin-bottom: 20px;
    animation: fadeIn 1s ease-out;
    background: linear-gradient(90deg, #00c3ff, #00ff88);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: none;
}

.banner-desc {
    font-size: 20px;
    margin-bottom: 30px;
    opacity: 0.9;
    animation: fadeIn 1s ease-out 0.2s both;
    color: #b0c4de;
}

.banner-btn {
    animation: fadeIn 1s ease-out 0.4s both;
}

/* 通用区块 - 科技风格 */
.section {
    padding: 80px 0;
    background: linear-gradient(180deg, #0a0f1a 0%, #0d1525 100%);
}

.about-section {
    background: linear-gradient(180deg, #0a0f1a 0%, #0f1a2a 100%);
}

.service-section {
    background: linear-gradient(135deg, #0a0f1a 0%, #0d1525 50%, #0a1628 100%);
    color: #fff;
    position: relative;
    overflow: hidden;
}

.service-section::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(0, 195, 255, 0.05) 0%, transparent 50%);
    animation: pulse 8s ease-in-out infinite;
}

.advantage-section {
    background: linear-gradient(180deg, #0d1525 0%, #0a0f1a 100%);
}

/* 产品区块 */
.product-section {
    background: linear-gradient(180deg, #0a0f1a 0%, #0d1525 100%);
}

.product-section-light {
    background: linear-gradient(180deg, #0f1a2a 0%, #0a0f1a 100%);
}

.product-content h3 {
    font-size: 28px;
    margin-bottom: 20px;
    color: #e0e6ed;
    background: linear-gradient(90deg, #00c3ff, #00ff88);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.product-content p {
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 25px;
    color: #8b9dc3;
}

.product-features {
    list-style: none;
    margin-bottom: 30px;
}

.product-features li {
    font-size: 15px;
    color: #b0c4de;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.product-image {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(0, 195, 255, 0.1) 0%, rgba(0, 255, 136, 0.1) 100%);
    border: 1px solid rgba(0, 195, 255, 0.3);
    border-radius: 16px;
    padding: 60px;
    min-height: 300px;
    box-shadow: 0 0 40px rgba(0, 195, 255, 0.1), inset 0 0 60px rgba(0, 195, 255, 0.05);
}

.contract-image {
    color: #00c3ff;
}

.catering-image {
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.1) 0%, rgba(0, 195, 255, 0.1) 100%);
    color: #00ff88;
}

.product-image i {
    opacity: 0.9;
    filter: drop-shadow(0 0 20px currentColor);
}

.partner-section {
    background: linear-gradient(180deg, #0d1525 0%, #0a0f1a 100%);
}

.contact-section {
    background: linear-gradient(180deg, #0a0f1a 0%, #0d1a2d 100%);
}

/* 区块标题 */
.section-title {
    text-align: center;
    margin-bottom: 60px;
}

.section-title h2 {
    font-size: 36px;
    color: #e0e6ed;
    margin-bottom: 10px;
}

.section-title p {
    font-size: 14px;
    color: #5a6a8a;
    letter-spacing: 4px;
}

.service-section .section-title h2,
.service-section .section-title p {
    color: #e0e6ed;
}

/* Z字形分栏布局 */
.split-row {
    display: flex;
    align-items: center;
    gap: 60px;
    margin-bottom: 80px;
}

.split-row.reverse {
    flex-direction: row-reverse;
}

.split-image-wrap {
    flex: 1;
}

.split-image-box {
    position: relative;
    height: 400px;
    background: linear-gradient(135deg, rgba(0, 195, 255, 0.08) 0%, rgba(0, 255, 136, 0.08) 100%);
    border: 1px solid rgba(0, 195, 255, 0.3);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.image-decoration {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(0, 195, 255, 0.1) 0%, transparent 50%);
    animation: float 6s ease-in-out infinite;
}

.image-placeholder {
    position: relative;
    z-index: 1;
    color: #00c3ff;
    filter: drop-shadow(0 0 30px rgba(0, 195, 255, 0.5));
}

.image-placeholder::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, transparent, #00c3ff, transparent);
    border-radius: 2px;
}

.split-text {
    flex: 1;
}

.split-tag {
    font-size: 12px;
    color: #00c3ff;
    letter-spacing: 3px;
    margin-bottom: 15px;
    font-weight: 600;
}

.split-text h3 {
    font-size: 32px;
    margin-bottom: 10px;
    color: #e0e6ed;
    background: linear-gradient(90deg, #00c3ff, #00ff88);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.split-text .highlight {
    font-size: 18px;
    color: #00c3ff;
    margin-bottom: 20px;
    font-weight: 500;
}

.split-text p {
    font-size: 15px;
    line-height: 1.8;
    margin-bottom: 15px;
    color: #8b9dc3;
}

.split-stats {
    display: flex;
    gap: 40px;
    margin-top: 30px;
    padding-top: 30px;
    border-top: 1px solid rgba(0, 195, 255, 0.2);
}

.split-stat-item {
    text-align: center;
}

.split-stat-item strong {
    display: block;
    font-size: 32px;
    background: linear-gradient(90deg, #00c3ff, #00ff88);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 5px;
}

.split-stat-item span {
    font-size: 13px;
    color: #5a6a8a;
}

/* 产品特性列表 */
.product-features.modern {
    list-style: none;
    margin: 25px 0;
}

.product-features.modern li {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 15px;
    color: #b0c4de;
}

.feature-dot {
    width: 8px;
    height: 8px;
    background: linear-gradient(135deg, #00c3ff, #00ff88);
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(0, 195, 255, 0.5);
}

.modern-btn {
    background: linear-gradient(135deg, rgba(0, 195, 255, 0.2) 0%, rgba(0, 255, 136, 0.2) 100%) !important;
    border: 1px solid #00c3ff !important;
    color: #00c3ff !important;
    border-radius: 8px;
    transition: all 0.3s;
}

.modern-btn:hover {
    background: linear-gradient(135deg, rgba(0, 195, 255, 0.4) 0%, rgba(0, 255, 136, 0.4) 100%) !important;
    box-shadow: 0 0 20px rgba(0, 195, 255, 0.3);
}

@keyframes float {
    0%, 100% {
        transform: translate(0, 0);
    }
    50% {
        transform: translate(20px, 20px);
    }
}

/* 关于我们 */
.about-text h3 {
    font-size: 28px;
    margin-bottom: 20px;
    color: #e0e6ed;
    background: linear-gradient(90deg, #00c3ff, #00ff88);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.about-text p {
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 15px;
    color: #8b9dc3;
}

.about-stats {
    display: flex;
    gap: 40px;
    margin-top: 30px;
}

.stat-item {
    text-align: center;
}

.stat-item strong {
    display: block;
    font-size: 36px;
    background: linear-gradient(90deg, #00c3ff, #00ff88);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 5px;
}

.stat-item span {
    font-size: 14px;
    color: #5a6a8a;
}

.about-image img {
    width: 100%;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 195, 255, 0.15);
    border: 1px solid rgba(0, 195, 255, 0.2);
}

/* 服务卡片 - 玻璃态 */
.service-card {
    position: relative;
    text-align: center;
    padding: 40px 20px;
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    margin-bottom: 20px;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    overflow: hidden;
}

.service-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #00c3ff, transparent);
    opacity: 0;
    transition: opacity 0.3s;
}

.service-card:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(0, 195, 255, 0.3);
    box-shadow: 0 20px 40px rgba(0, 195, 255, 0.15);
}

.service-card:hover::before {
    opacity: 1;
}

.service-icon {
    margin-bottom: 20px;
    color: #00c3ff;
    filter: drop-shadow(0 0 15px rgba(0, 195, 255, 0.5));
}

.service-card h3 {
    font-size: 20px;
    margin-bottom: 15px;
    color: #e0e6ed;
}

.service-card p {
    font-size: 14px;
    line-height: 1.6;
    color: #8b9dc3;
}

.service-hover {
    display: none;
}

/* 优势卡片 */
.advantage-card {
    text-align: center;
    padding: 40px 20px;
    background: linear-gradient(135deg, rgba(0, 195, 255, 0.05) 0%, rgba(0, 255, 136, 0.05) 100%);
    border: 1px solid rgba(0, 195, 255, 0.2);
    border-radius: 16px;
    margin-bottom: 20px;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.advantage-card:hover {
    transform: translateY(-5px) scale(1.02);
    border-color: rgba(0, 195, 255, 0.5);
    box-shadow: 0 15px 40px rgba(0, 195, 255, 0.2);
}

.advantage-number {
    font-size: 48px;
    font-weight: bold;
    background: linear-gradient(90deg, #00c3ff, #00ff88);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 20px;
}

.advantage-card h3 {
    font-size: 20px;
    color: #e0e6ed;
    margin-bottom: 15px;
}

.advantage-card p {
    font-size: 14px;
    color: #8b9dc3;
    line-height: 1.6;
}

/* 合作伙伴 */
.partner-logo {
    text-align: center;
    padding: 30px 20px;
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    transition: all 0.3s;
}

.partner-logo:hover {
    border-color: #00c3ff;
    box-shadow: 0 4px 20px rgba(0, 195, 255, 0.2);
    background: rgba(0, 195, 255, 0.05);
}

.partner-logo span {
    display: block;
    margin-top: 10px;
    font-size: 14px;
    color: #8b9dc3;
}

/* 联系方式 */
.contact-info {
    padding: 20px;
}

.contact-item {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 30px;
}

.contact-icon {
    color: #00c3ff;
    flex-shrink: 0;
    filter: drop-shadow(0 0 10px rgba(0, 195, 255, 0.5));
}

.contact-item h4 {
    font-size: 16px;
    color: #e0e6ed;
    margin-bottom: 5px;
}

.contact-item p {
    font-size: 14px;
    color: #8b9dc3;
}

.contact-qrcode {
    text-align: center;
    padding: 40px 20px;
    background: rgba(0, 195, 255, 0.05);
    border: 1px dashed rgba(0, 195, 255, 0.3);
    border-radius: 16px;
}

.qrcode-img {
    width: 200px;
    height: 200px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 195, 255, 0.2);
    margin-bottom: 20px;
}

.qrcode-tip {
    font-size: 14px;
    color: #5a6a8a;
    margin-top: 10px;
}

/* 底部 */
.footer {
    background: linear-gradient(180deg, #0d1a2d 0%, #080d17 100%);
    color: #e0e6ed;
    padding: 60px 0 30px;
    border-top: 1px solid rgba(0, 195, 255, 0.2);
}

.footer-brand {
    margin-bottom: 20px;
}

.footer-logo {
    width: 80px;
    margin-bottom: 10px;
    filter: drop-shadow(0 0 10px rgba(0, 195, 255, 0.3));
}

.footer-brand p {
    font-size: 14px;
    color: #5a6a8a;
}

.footer-links h4 {
    font-size: 16px;
    margin-bottom: 20px;
    color: #00c3ff;
}

.footer-links ul {
    list-style: none;
}

.footer-links li {
    margin-bottom: 10px;
}

.footer-links a {
    color: #8b9dc3;
    font-size: 14px;
    transition: all 0.3s;
}

.footer-links a:hover {
    color: #00c3ff;
    text-shadow: 0 0 10px rgba(0, 195, 255, 0.5);
}

.footer-contact h4 {
    font-size: 16px;
    margin-bottom: 20px;
    color: #00c3ff;
}

.footer-contact p {
    font-size: 14px;
    color: #8b9dc3;
    margin-bottom: 10px;
}

.footer-bottom {
    margin-top: 40px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    text-align: center;
}

.footer-bottom p {
    font-size: 14px;
    color: #5a6a8a;
}

.footer-bottom a {
    color: #8b9dc3;
    margin: 0 10px;
    transition: color 0.3s;
}

.footer-bottom a:hover {
    color: #00c3ff;
}

/* 返回顶部 */
.back-top {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, rgba(0, 195, 255, 0.2) 0%, rgba(0, 255, 136, 0.2) 100%);
    border: 1px solid rgba(0, 195, 255, 0.5);
    color: #00c3ff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 195, 255, 0.3);
    transition: all 0.3s;
}

.back-top:hover {
    background: linear-gradient(135deg, rgba(0, 195, 255, 0.4) 0%, rgba(0, 255, 136, 0.4) 100%);
    box-shadow: 0 4px 30px rgba(0, 195, 255, 0.5);
    transform: translateY(-3px);
}

/* 动画 */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
        opacity: 0.5;
    }
    50% {
        transform: scale(1.2);
        opacity: 0.8;
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .header-content {
        padding: 15px;
    }
    
    .banner-section {
        margin-top: 60px;
    }
    
    .banner-item {
        height: 400px;
    }
    
    .banner-title {
        font-size: 32px;
    }
    
    .banner-desc {
        font-size: 16px;
    }
    
    .section {
        padding: 60px 0;
    }
    
    .section-title h2 {
        font-size: 28px;
    }
    
    /* Z字形布局响应式 */
    .split-row {
        flex-direction: column;
        gap: 30px;
        margin-bottom: 60px;
    }
    
    .split-row.reverse {
        flex-direction: column;
    }
    
    .split-image-box {
        height: 250px;
    }
    
    .split-stats {
        flex-wrap: wrap;
        gap: 20px;
        justify-content: center;
    }
    
    .split-stat-item {
        min-width: 80px;
    }
    
    .split-stat-item strong {
        font-size: 24px;
    }
    
    .about-stats {
        flex-direction: column;
        gap: 20px;
    }
    
    .footer-bottom {
        text-align: left;
    }
    
    .footer-bottom a {
        display: block;
        margin: 10px 0;
    }
}
</style>
